import { CAPABILITIES, ROLES, can, toRole } from '../helpers/roles';

/**
 * The Hub sidebar, as data.
 *
 * A manifest rather than route metadata, because three things here have no
 * route to hang metadata on or span more than one:
 *
 *   - Overview is visible but not yet built, so there is no route record to
 *     carry its label or icon.
 *   - A section can own more than one route (Sessions owns /sessions and its
 *     create and edit pages), and a nav item is not a route.
 *   - Order and grouping are properties of the menu, not of any page. The old
 *     nav used `meta.group` for both grouping AND "is this in the sidebar at
 *     all", which is why removing a group number silently removed an item.
 *
 * The link back to routing is one-way and asserted: routes declare
 * `meta.navItem: '<id>'`, this file declares `id` and `to`, and
 * tests/navigation.test.js fails if the two drift. That is what keeps two
 * sources of truth from becoming two versions of the truth.
 *
 * `icon` names a shape in HubNavIcon (the prototype's outline set): the same
 * icon wherever an item appears, for every role, open or folded.
 *
 * `capability` is the same constant the router guard checks, so what the
 * sidebar hides and what the guard blocks cannot disagree — a test asserts
 * that too. Hiding a link is not a permission; both halves are required.
 */
export const NAV_ITEMS = [
  {
    id: 'overview',
    page: 'overview',
    label: 'Overview',
    group: 1,
    to: '/overview',
    // No capability: the Overview is every staff role's landing page, and a
    // broker's is their own book's version of it.
    icon: 'home',
  },
  {
    id: 'policyholders',
    page: 'policyholders',
    label: 'Policyholders',
    group: 1,
    to: '/policyholders',
    badge: 'access_requests',
    icon: 'users',
  },
  {
    id: 'messages',
    page: 'messages',
    label: 'Messages',
    group: 1,
    to: '/messages',
    // A key, not a count: useHubBadges() resolves it. It counts conversations
    // with a policyholder message nobody at the agency has opened yet.
    badge: 'messages',
    icon: 'mail',
  },
  {
    id: 'communications',
    page: 'communications',
    label: 'Communications',
    group: 1,
    to: '/communications',
    // No capability: a broker reaches the read surface, and each route behind
    // it narrows to them. Defining a segment or running a sequence is still
    // tenant-admin, and the tab renders no control for either.
    icon: 'megaphone',
  },
  {
    id: 'sessions',
    page: 'agenda',
    label: 'Sessions',
    group: 2,
    to: '/sessions',
    icon: 'video',
  },
  {
    id: 'recordings',
    page: 'recordings',
    label: 'Recordings',
    group: 2,
    to: '/recordings',
    icon: 'film',
  },
  {
    id: 'content',
    page: 'resources',
    label: 'Content',
    group: 2,
    to: '/content',
    icon: 'book',
  },
  {
    id: 'insights',
    page: 'analytics',
    label: 'Insights',
    group: 2,
    // Not in this phase: greyed in the sidebar with nothing to click, for
    // every hub role, and its routes say the same on the page (routes.js,
    // InsightsSoon). To bring Insights back, restore `to: '/insights'` here
    // and real pages on those routes (see the note in routes.js).
    disabled: true,
    to: null,
    // What it says when hovered, focused or tapped, so a greyed item is never
    // a dead end with no reason given.
    soon: "Insights aren't available yet",
    icon: 'chart',
  },
  {
    id: 'policy-lines',
    label: 'Policy lines',
    // Has no page of its own to grant: it lists policyholders' policies, so
    // hub staff reach it with the policyholders page.
    grant: 'policyholders',
    group: 3,
    to: '/policy-lines',
    icon: 'shield',
  },
  {
    id: 'registration',
    page: 'registration',
    label: 'Registration',
    group: 3,
    to: '/registration',
    capability: CAPABILITIES.MANAGE_HUB_CONTENT,
    icon: 'door',
  },
  {
    id: 'settings',
    page: 'settings',
    label: 'Settings',
    group: 3,
    to: '/settings',
    capability: CAPABILITIES.MANAGE_HUB_CONTENT,
    icon: 'sliders',
  },
];

/**
 * `pages` is the hub's SSO page grant (its slugs are `page` above), and null
 * means unrestricted. Only hub staff are held to it: an owner has every page
 * and a broker's set is fixed by their role.
 */
export const pageGranted = (item, role, pages) => {
  const slug = item?.page ?? item?.grant;

  return !slug || !Array.isArray(pages) || toRole(role) !== ROLES.HUB_STAFF || pages.includes(slug);
};

/**
 * The items this viewer may see.
 *
 * Pure, so it is the unit under test for the first acceptance criterion: a
 * broker's sidebar has no Registration or Settings.
 */
export const visibleNavItems = (role, capabilities = null, pages = null) =>
  NAV_ITEMS.filter(
    (item) => (!item.capability || can(role, item.capability, capabilities)) && pageGranted(item, role, pages)
  );

export const navItemById = (id) => NAV_ITEMS.find((item) => item.id === id) ?? null;

/** Grouped for rendering, preserving manifest order and dropping empty groups. */
export const groupNavItems = (items) =>
  Object.values(
    items.reduce((groups, item) => {
      (groups[item.group] = groups[item.group] || []).push(item);
      return groups;
    }, {})
  );
